import React from 'react';
import classNames from 'classnames';

import {Button, Icon, Text} from '@albert-io/atomic';
import {useAnnotationModeStore} from 'client/components/PracticeView/Annotations/AnnotationModeStore';
import {selectHasAnnotatableContent} from 'client/components/PracticeView/Annotations/annotationSelectors';
import {useIsAnnotationModeAvailable} from 'client/components/PracticeView/Annotations/useIsAnnotationModeAvailable';

export default function AnnotationModeBanner() {
  const isAnnotationModeAvailable = useIsAnnotationModeAvailable();
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const toggleAnnotationMode = useAnnotationModeStore((state) => state.toggleAnnotationMode);
  const hasContent = useAnnotationModeStore(selectHasAnnotatableContent);

  if (!isAnnotationModeAvailable) {
    return null;
  }

  const {icon, tone, detail} = hasContent
    ? ({icon: 'highlighter', tone: 'primary', detail: 'Some interactions are disabled'} as const)
    : ({icon: 'exclamation-triangle', tone: 'warning', detail: 'No content to highlight'} as const);

  return (
    // Kept mounted (inert while off) so it can animate in/out from the bottom.
    <div
      role='status'
      ref={(el) => {
        if (el) {
          el.inert = !isAnnotationModeOn;
        }
      }}
      className={classNames(
        'inset-x-x2 bottom-x11 s:bottom-x4 gap-x2 rounded-x4 background-highlight pl-x3 pr-x2 py-x1-half shadow-l2 duration-[250ms] fixed z-50 mx-auto flex max-w-[554px] items-center transition-all ease-out',
        {
          'translate-y-full pointer-events-none opacity-0': !isAnnotationModeOn
        }
      )}
    >
      <Icon icon={icon} iconStyle='regular' color={tone} />
      <div className='flex min-w-0 grow flex-col'>
        <Text color='primary' className='text-m'>
          Annotation mode active
        </Text>
        <Text color={tone} className='text-s'>
          {detail}
        </Text>
      </div>
      <Button
        as='button'
        size='m'
        variant='solid'
        color='blend-light'
        className='rounded-x3 shrink-0 whitespace-nowrap'
        onClick={toggleAnnotationMode}
      >
        Done Annotating
      </Button>
    </div>
  );
}
